import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ChevronLeft, Plus, Trash2, Save, Building2, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Header field definitions for the COA — three columns of label-value pairs
// mirroring the printed layout. Each entry is [key, label].
const COL1 = [
  ["product_name", "Product Name"],
  ["packing", "Packing"],
  ["generic_name", "Generic Name"],
  ["product_code", "Product Code"],
  ["batch_no", "Batch No."],
  ["actual_batch_size", "Actual Batch Size"],
  ["sample_size", "Sample Size"],
  ["released_qty", "Released Qty"],
  ["remarks", "Remarks"],
];
const COL2 = [
  ["mfg_date", "Mfg. Dt."],
  ["exp_date", "Exp. Dt."],
  ["test_packing", "Test Packing"],
  ["mfg_lic_no", "Mfg. Lic. No."], // virtual — bound to license_no
  ["test_as_per", "Test As Per"],
];
const COL3 = [
  ["ar_no", "A.R. No."],
  ["rel_dt", "Rel. Dt."],
  ["tr_slip_no", "T.R. Slip No."],
  ["tr_slip_dt", "T.R. Slip Dt."],
  ["analysis_date", "Analysis Date"],
  ["specification_no", "Specification No."],
  ["specification_dt", "Specification Dt."],
  ["stp_no", "STP No."],
  ["location", "Location"],
  ["make", "Make"],
];

export default function CoaForm({ initial, companies, onCancel, onSave, onOpenCompanyMgr, token }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);

  useEffect(() => { setForm(initial); }, [initial]);

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const onCompanyChange = (id) => {
    const c = companies.find((x) => x.id === id);
    if (!c) { set("company_id", ""); return; }
    setForm((p) => ({
      ...p,
      company_id: c.id,
      company_name: c.name || "",
      license_no: c.license_no || "",
      footer_code: c.footer_code || p.footer_code,
      department_text: c.department_text || p.department_text,
      act_text: c.act_text || p.act_text,
      conclusion: p.conclusion || c.default_conclusion || "",
      prepared_by: p.prepared_by || c.default_prepared_by || "",
      prepared_role: p.prepared_role || c.default_prepared_role || "",
      checked_by: p.checked_by || c.default_checked_by || "",
      checked_role: p.checked_role || c.default_checked_role || "",
      approved_by: p.approved_by || c.default_approved_by || "",
      approved_role: p.approved_role || c.default_approved_role || "",
    }));
    toast.success(`Loaded ${c.name} — license auto-filled`);
  };

  const tryLoadTemplate = async () => {
    const pname = (form.product_name || "").trim();
    if (!pname) { toast.error("Enter the product name first"); return; }
    setTemplateLoading(true);
    try {
      const params = new URLSearchParams({ product_name: pname });
      if (form.company_id) params.set("company_id", form.company_id);
      const r = await fetch(`${API}/api/documents/coa/templates/match?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 404) { toast.info("No saved template for this product yet"); return; }
      if (!r.ok) { toast.error("Template lookup failed"); return; }
      const t = await r.json();
      setForm((p) => ({
        ...p,
        tests: t.tests?.length ? t.tests : p.tests,
        generic_name: p.generic_name || t.generic_name || "",
        packing: p.packing || t.packing || "",
        product_code: p.product_code || t.product_code || "",
        test_as_per: p.test_as_per || t.test_as_per || "",
        conclusion: p.conclusion || t.conclusion || "",
      }));
      toast.success(`Template loaded · ${t.tests?.length || 0} test rows`);
    } finally {
      setTemplateLoading(false);
    }
  };

  const addTestRow = () => set("tests", [...(form.tests || []), { test: "", result: "", specification: "" }]);
  const updateTest = (idx, key, val) => set("tests", form.tests.map((t, i) => (i === idx ? { ...t, [key]: val } : t)));
  const removeTest = (idx) => set("tests", form.tests.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!form.product_name?.trim()) { toast.error("Product name is required"); return; }
    setSaving(true);
    try { await onSave(form); }
    finally { setSaving(false); }
  };

  const renderHeaderField = ([key, label]) => {
    // virtual field: Mfg. Lic. No is bound to license_no
    const isLic = key === "mfg_lic_no";
    const val = isLic ? (form.license_no || "") : (form[key] || "");
    const onChange = (e) => set(isLic ? "license_no" : key, e.target.value);
    return (
      <div key={key} className="grid grid-cols-[140px_8px_1fr] items-center gap-1 text-sm">
        <Label className="font-semibold text-xs uppercase tracking-tight">{label}</Label>
        <span className="text-muted-foreground">:</span>
        <Input
          value={val}
          onChange={onChange}
          className="h-7 text-sm font-mono"
          data-testid={`coa-field-${key}`}
          placeholder={isLic ? "auto-filled from company" : ""}
        />
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="coa-form">
      {/* Top bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} data-testid="coa-back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h2 className="text-base sm:text-lg font-semibold">{form.id ? "Edit COA" : "New COA"}</h2>
        </div>
        <Button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="coa-save-btn">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save & Preview"}
        </Button>
      </div>

      {/* Company + title */}
      <Card>
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
            <div>
              <Label className="text-xs">Company *</Label>
              <select
                value={form.company_id || ""}
                onChange={(e) => onCompanyChange(e.target.value)}
                className="w-full h-9 px-2 rounded-md border bg-background text-sm"
                data-testid="coa-company-select"
              >
                <option value="">— Select company —</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <Button variant="outline" size="sm" onClick={onOpenCompanyMgr} className="gap-1.5" data-testid="coa-manage-companies-btn">
              <Building2 className="w-4 h-4" /> Manage
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Document Title</Label>
              <Input value={form.document_title} onChange={(e) => set("document_title", e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Footer Code</Label>
              <Input value={form.footer_code || ""} onChange={(e) => set("footer_code", e.target.value)} className="h-8 text-sm" placeholder="FGANLCERTQA" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Header fields in 3 columns — mirrors the printed layout */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="space-y-2">{COL1.map(renderHeaderField)}</div>
            <div className="space-y-2">{COL2.map(renderHeaderField)}</div>
            <div className="space-y-2">{COL3.map(renderHeaderField)}</div>
          </div>
        </CardContent>
      </Card>

      {/* Test table */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm">Test Results</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={tryLoadTemplate} disabled={templateLoading} className="gap-1.5" data-testid="coa-load-template-btn">
                <Sparkles className="w-3.5 h-3.5" />
                {templateLoading ? "Loading…" : "Load template"}
              </Button>
              <Button size="sm" onClick={addTestRow} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="coa-add-test-row-btn">
                <Plus className="w-3.5 h-3.5" /> Add Row
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto rounded border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-2 py-1.5 w-10">Sr.</th>
                  <th className="px-2 py-1.5">Test</th>
                  <th className="px-2 py-1.5">Result</th>
                  <th className="px-2 py-1.5">Specification</th>
                  <th className="px-2 py-1.5 w-8" />
                </tr>
              </thead>
              <tbody>
                {form.tests.map((t, i) => (
                  <tr key={i} className="border-t align-top" data-testid={`coa-test-row-${i}`}>
                    <td className="px-2 py-1 text-center font-mono">{i + 1}</td>
                    <td className="px-1 py-1">
                      <Textarea value={t.test} onChange={(e) => updateTest(i, "test", e.target.value)} rows={1} className="text-xs min-h-[32px] resize-y" data-testid={`coa-test-name-${i}`} />
                    </td>
                    <td className="px-1 py-1">
                      <Textarea value={t.result} onChange={(e) => updateTest(i, "result", e.target.value)} rows={1} className="text-xs min-h-[32px] resize-y" data-testid={`coa-test-result-${i}`} />
                    </td>
                    <td className="px-1 py-1">
                      <Textarea value={t.specification} onChange={(e) => updateTest(i, "specification", e.target.value)} rows={1} className="text-xs min-h-[32px] resize-y" data-testid={`coa-test-spec-${i}`} />
                    </td>
                    <td className="px-1 py-1 text-center">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => removeTest(i)} data-testid={`coa-test-remove-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {form.tests.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-xs text-muted-foreground py-4">No rows. Click <b>Add Row</b> or <b>Load template</b>.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Checkbox id="save-as-template" checked={!!form.save_as_template} onCheckedChange={(v) => set("save_as_template", v)} data-testid="coa-save-template-cb" />
            <label htmlFor="save-as-template" className="text-xs cursor-pointer">Save these test rows as a template for this product (auto-fills next time)</label>
          </div>
        </CardContent>
      </Card>

      {/* Conclusion + sign-offs */}
      <Card>
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div>
            <Label className="text-xs">Conclusion</Label>
            <Textarea value={form.conclusion} onChange={(e) => set("conclusion", e.target.value)} rows={2} className="text-sm" placeholder="The above sample complies as per USP" data-testid="coa-conclusion-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Signoff label="Prepared By" name={form.prepared_by} role={form.prepared_role} onName={(v) => set("prepared_by", v)} onRole={(v) => set("prepared_role", v)} k="prepared" />
            <Signoff label="Checked By" name={form.checked_by} role={form.checked_role} onName={(v) => set("checked_by", v)} onRole={(v) => set("checked_role", v)} k="checked" />
            <Signoff label="Approved By" name={form.approved_by} role={form.approved_role} onName={(v) => set("approved_by", v)} onRole={(v) => set("approved_role", v)} k="approved" />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end pb-4">
        <Button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="coa-save-btn-bottom">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save & Preview"}
        </Button>
      </div>
    </div>
  );
}

const Signoff = ({ label, name, role, onName, onRole, k }) => (
  <div className="border rounded-lg p-3 bg-muted/30 space-y-1.5">
    <Label className="text-xs font-semibold">{label}</Label>
    <Input value={name} onChange={(e) => onName(e.target.value)} placeholder="Name" className="h-8 text-sm" data-testid={`coa-${k}-name`} />
    <Input value={role} onChange={(e) => onRole(e.target.value)} placeholder="Role / designation" className="h-8 text-sm" data-testid={`coa-${k}-role`} />
  </div>
);
